import { AwsRumConfig } from 'aws-rum-web';
import { RIP_REGION_CONFIG } from './regionConfig.generated';

// TODO: replace with your personal AWS account when trying to test locally
const PERSONAL_DEV_ACCOUNT = '352790642107';
const BETA_ENDPOINT = 'beta.hammerstone.us-east-1.data-tools.aws.a2z.com';
const GAMMA_ENDPOINT = 'gamma.hammerstone.us-east-1.data-tools.aws.a2z.com';
const PROD_ENDPOINT = 'hammerstone.us-east-1.data-tools.aws.a2z.com';

// Set the same number of standard retries as Midway library
// https://tiny.amazon.com/16bxbt95k/codeamazpackMidwblobef29src
export const AWS_CREDS_NUM_RETRIES = 3;

export const HAMMERSTONE_ADMIN_GROUP = 'AWS-DW-PRIMARY';

/**
 * Get current URL domain of the endpoint
 *
 * @returns {string} Endpoint domain of the service or 'devDesktop' for local cloud desktops
 */
export const getServiceDomain = (): string => {
	const domain = window.location.hostname;

	// Account for Dev-desktop endpoints
	if (domain.includes('.corp.amazon.com')) {
		return 'devDesktop';
	}

	return domain;
};

/**
 * Get region from URL
 * e.g. https://dev-dsk-sjzhang-2a-903d4059.us-west-2.amazon.com
 * e.g. https://beta.hammerstone.us-east-1.data-tools.aws.a2z.com
 * e.g. https://hammerstone.us-east-1.data-tools.aws.a2z.com
 * e.g. https://us-west-2.prod.hammerstone.data-tools.aws.a2z.com (new pattern)
 *
 * @returns {string} Region e.g. us-east-1
 */
export const getServiceRegion = (): string => {
	const domain = window.location.hostname;
	const subdomains = domain.split('.');

	// this should never happen, unless Route53 settings are messed up
	if (subdomains.length <= 0) {
		throw URIError("Domain is not loaded?! Check browser's endpoint settings");
	}

	// New pattern: <region>.<stage>.hammerstone.<rootDomain>
	if (subdomains[2] === 'hammerstone') {
		return subdomains[0];
	}

	// Legacy pattern: hammerstone.<region>.<rootDomain> (prod)
	// or <stage>.hammerstone.<region>.<rootDomain> (non-prod)
	if (subdomains[1] === 'hammerstone') {
		return subdomains[2];
	}

	if (domain.includes('.corp.amazon.com')) {
		return 'us-east-1';
	}

	return subdomains[1];
};

// Used for various headers
export const WEBSITE_NAME = 'Hammerstone';

/**
 * Deployment stage derived from the current hostname.
 *
 * Stage cannot be inferred from region alone: IAD's beta/gamma/prod (and dev desktop) all share
 * region us-east-1, so we read the stage from the hostname. Prod config is then resolved by
 * region (see RIP_REGION_CONFIG); non-prod stages use the hardcoded stage fallbacks below.
 *
 * The stage lives in a different hostname label depending on the endpoint pattern (mirrors the
 * pattern handling in getServiceRegion):
 *   - New pattern:        <region>.<stage>.hammerstone.<rootDomain>   (stage = label[1])
 *   - Legacy non-prod:    <stage>.hammerstone.<region>.<rootDomain>   (stage = label[0])
 *   - Legacy prod:        hammerstone.<region>.<rootDomain>           (prod)
 */
export type ServiceStage = 'prod' | 'gamma' | 'beta' | 'devDesktop';

const normalizeStage = (label: string): ServiceStage => {
	if (label === 'beta') return 'beta';
	if (label === 'gamma') return 'gamma';
	return 'prod';
};

export const getServiceStage = (): ServiceStage => {
	const domain = window.location.hostname;
	if (domain.includes('.corp.amazon.com')) {
		return 'devDesktop';
	}

	const subdomains = domain.split('.');

	// New pattern: <region>.<stage>.hammerstone.<rootDomain> — stage is the 2nd label
	if (subdomains[2] === 'hammerstone') {
		return normalizeStage(subdomains[1]);
	}

	// Legacy non-prod: <stage>.hammerstone.<region>.<rootDomain> — stage is the 1st label
	if (subdomains[1] === 'hammerstone') {
		return normalizeStage(subdomains[0]);
	}

	// Legacy prod: hammerstone.<region>.<rootDomain> — 'hammerstone' is the 1st label, no stage prefix
	if (subdomains[0] === 'hammerstone') {
		return 'prod';
	}

	// Unrecognized hostname. Default to prod (the safest serving choice — prod is where the app is
	// actually deployed, and prod-config lookups still return undefined for an unknown host, so this
	// can't silently point a page at the wrong endpoints). We log a warning because an unknown host
	// means our stage-detection patterns are likely out of date and should be investigated.
	console.warn(`getServiceStage: unrecognized hostname '${domain}', defaulting to 'prod'`);
	return 'prod';
};

/**
 * Maps a react-ui prod hostname to its region, built from RIP_REGION_CONFIG. Used to resolve the
 * current window.location.hostname to a region for prod config lookups.
 */
const REACT_UI_DOMAIN_TO_REGION: { [domain: string]: string } = {};
for (const [region, cfg] of Object.entries(RIP_REGION_CONFIG)) {
	REACT_UI_DOMAIN_TO_REGION[cfg.reactUiDomain] = region;
}

/** Region for the current prod hostname, or undefined if unknown. */
const getProdRegion = (): string | undefined => REACT_UI_DOMAIN_TO_REGION[window.location.hostname];

// Old Hammerstone + Permissions Tool endpoints for non-prod stages (not modeled in RIP).
// Dev desktop, beta and (new) gamma all point at legacy Gamma.
const OLD_HAMMERSTONE_NON_PROD: Record<Exclude<ServiceStage, 'prod'>, { hsEndpoint: string; ptEndpoint: string }> = {
	devDesktop: {
		hsEndpoint: 'https://hammerstonegamma.amazon.com',
		ptEndpoint: 'https://hammerstone-permissions-gamma-iad.iad.proxy.amazon.com',
	},
	beta: {
		hsEndpoint: 'https://hammerstonegamma.amazon.com',
		ptEndpoint: 'https://hammerstone-permissions-gamma-iad.iad.proxy.amazon.com',
	},
	gamma: {
		hsEndpoint: 'https://hammerstonegamma.amazon.com',
		ptEndpoint: 'https://hammerstone-permissions-gamma-iad.iad.proxy.amazon.com',
	},
};

// Prod overrides keyed by region. Pinned because RIP's data for these regions is being corrected
// in CR-297195498 — remove an override once RIP holds the correct value.
const OLD_HAMMERSTONE_PROD_OVERRIDES: { [region: string]: { hsEndpoint: string; ptEndpoint: string } } = {
	// PDX: RIP's permissions_tool_endpoint is currently wrong; pin until CR-297195498 synthesizes.
	'us-west-2': {
		hsEndpoint: 'https://hammerstone-pdx.pdx.proxy.amazon.com',
		ptEndpoint: 'https://hammerstone-permissions-pdx.pdx.proxy.amazon.com',
	},
};

/**
 * Resolve the old Hammerstone + Permissions Tool endpoints for the current stage/region.
 * Non-prod resolves by stage; prod resolves by region (pinned override first, then RIP).
 */
const getOldHammerstoneConfig = (): { hsEndpoint: string; ptEndpoint: string } | undefined => {
	const stage = getServiceStage();
	if (stage !== 'prod') {
		return OLD_HAMMERSTONE_NON_PROD[stage];
	}
	const region = getProdRegion();
	if (!region) return undefined;
	return OLD_HAMMERSTONE_PROD_OVERRIDES[region] ?? RIP_REGION_CONFIG[region]?.oldHammerstone;
};

export const getOldHammerstoneEndpoint = (): any => getOldHammerstoneConfig()?.hsEndpoint;

export const getOldPermissionsToolEndpoint = (): any => getOldHammerstoneConfig()?.ptEndpoint;

// Hammerstone Exposed RESTful Service endpoints
// TODO: Replace dev desktop and Prod configs with the actual configs
// TODO: Look into potential solution of calling AWS CLI to get this information.
// The CDK must be deployed through a stage first before you can fill out the information in these configs.
// After the CDK is deployed, go to Cloudwatch RUM on the AWS console and click on the monitor that was
// created by the CDK. Click on "Configuration" and then click on "Javascript Snippet" on the sidebar.
// The Javascript snippet will contain an application id, application version, application region, and
// AwsRumConfig, which you can copy into the config below.
const AWS_RUM_CONFIG: {
	[key: string]: {
		applicationId: String;
		applicationVersion: String;
		applicationRegion: String;
		awsRumConfig: AwsRumConfig;
	};
} = {
	// Dev Desktops
	devDesktop: {
		applicationId: '3386ab2f-8270-4fa1-bf92-9c487a9da43a',
		applicationVersion: '1.0.0',
		applicationRegion: 'us-east-1',
		awsRumConfig: {
			sessionSampleRate: 1,
			guestRoleArn: 'arn:aws:iam::734789510895:role/RUM-Monitor-us-east-1-734789510895-1332921156761-Unauth',
			identityPoolId: 'us-east-1:1dfc82f9-d6d5-4aab-80bd-438ce62bd6b1',
			endpoint: 'https://dataplane.rum.us-east-1.amazonaws.com',
			telemetries: ['performance', 'errors', 'http'],
			allowCookies: true,
			enableXRay: false,
		},
	},
	// Beta Endpoints
	[BETA_ENDPOINT]: {
		applicationId: '0576c7ae-cf26-4c01-9362-caa7cd9f4f1e',
		applicationVersion: '1.0.0',
		applicationRegion: 'us-east-1',
		awsRumConfig: {
			sessionSampleRate: 1,
			guestRoleArn: 'arn:aws:iam::734789510895:role/RUM-Monitor-us-east-1-cognitoUnauthenticatedRole',
			identityPoolId: 'us-east-1:da15ae0f-8336-4525-9622-eecb8ed62328',
			endpoint: 'https://dataplane.rum.us-east-1.amazonaws.com',
			telemetries: ['errors', 'performance', 'http'],
			allowCookies: true,
			enableXRay: true,
		},
	},
	// Gamma Endpoints
	[GAMMA_ENDPOINT]: {
		applicationId: '2de12002-e4be-4c4a-9ee2-7c4e8efe19af',
		applicationVersion: '1.0.0',
		applicationRegion: 'us-east-1',
		awsRumConfig: {
			sessionSampleRate: 1,
			guestRoleArn: 'arn:aws:iam::661504521297:role/RUM-Monitor-us-east-1-cognitoUnauthenticatedRole',
			identityPoolId: 'us-east-1:84c92a3a-ec3b-4dc4-ab09-a6369a788dc8',
			endpoint: 'https://dataplane.rum.us-east-1.amazonaws.com',
			telemetries: ['errors', 'performance', 'http'],
			allowCookies: true,
			enableXRay: true,
		},
	},
	// Prod Endpoints
	[PROD_ENDPOINT]: {
		applicationId: '511b863e-01d2-4f85-a58d-1432cfe1fdde',
		applicationVersion: '1.0.0',
		applicationRegion: 'us-east-1',
		awsRumConfig: {
			sessionSampleRate: 1,
			guestRoleArn: 'arn:aws:iam::057244456334:role/RUM-Monitor-us-east-1-cognitoUnauthenticatedRole',
			identityPoolId: 'us-east-1:32573083-d841-412f-89ab-c716185ff3cf',
			endpoint: 'https://dataplane.rum.us-east-1.amazonaws.com',
			telemetries: ['errors', 'performance', 'http'],
			allowCookies: true,
			enableXRay: true,
		},
	},
	// TODO: add other regions
};

// Hammerstone Exposed RESTful Service (HSAPI) client config.
interface HsApiRuntimeConfig {
	endpoint: string;
	region: string;
	iamRolePartition: string;
	iamRoleAccount: string;
}

// HSAPI config for non-prod stages (not modeled in RIP).
const HS_API_NON_PROD: Record<Exclude<ServiceStage, 'prod'>, HsApiRuntimeConfig> = {
	devDesktop: {
		endpoint: 'https://beta.us-east-1.api.data-tools.aws.a2z.com',
		region: 'us-east-1',
		iamRolePartition: 'aws',
		iamRoleAccount: PERSONAL_DEV_ACCOUNT,
	},
	beta: {
		endpoint: 'https://beta.us-east-1.api.data-tools.aws.a2z.com',
		region: 'us-east-1',
		iamRolePartition: 'aws',
		iamRoleAccount: '734789510895',
	},
	gamma: {
		endpoint: 'https://gamma.us-east-1.api.data-tools.aws.a2z.com',
		region: 'us-east-1',
		iamRolePartition: 'aws',
		iamRoleAccount: '661504521297',
	},
};

// Prod HSAPI overrides keyed by region. Pinned because RIP's hammerstone-api endpoints for these
// regions are being corrected in CR-297195498 — remove an override once RIP holds the correct value.
//
// NOTE: ZHY (cn-northwest-1) has no HSAPI entry — RIP lacks the ZHY react-ui account, so the
// generator emits no hsApi for it and there is no pin here. ZHY HSAPI is unavailable until that
// account is added to RIP. (ZHY old-Hammerstone/PT still work — sourced from RIP.)
const HS_API_PROD_OVERRIDES: { [region: string]: HsApiRuntimeConfig } = {
	'us-west-2': {
		endpoint: 'https://prod.pdx.hammerstone.api.data-tools.aws.a2z.com',
		region: 'us-west-2',
		iamRolePartition: 'aws',
		iamRoleAccount: '431997621865',
	},
	'eusc-de-east-1': {
		endpoint: 'https://prod.eusc-de-east-1.api.data-tools.a2z.eu',
		region: 'eusc-de-east-1',
		iamRolePartition: 'aws-eusc',
		iamRoleAccount: '553942257583',
	},
};

/**
 * Resolve the HSAPI client config for the current stage/region.
 * Non-prod resolves by stage; prod resolves by region (pinned override first, then RIP).
 */
export const getHSApiConfig = (): any => {
	const stage = getServiceStage();
	if (stage !== 'prod') {
		return HS_API_NON_PROD[stage];
	}
	const region = getProdRegion();
	if (!region) return undefined;
	const override = HS_API_PROD_OVERRIDES[region];
	if (override) return override;
	const cfg = RIP_REGION_CONFIG[region]?.hsApi;
	return cfg ? { region, ...cfg } : undefined;
};

export const getServicePartition = (): string => getHSApiConfig()?.iamRolePartition;

export const getAwsRumConfig = (): any => {
	const domain: string = getServiceDomain();
	return AWS_RUM_CONFIG[domain];
};

export const getHsApiClientIamRoleArn = (partition: string, account: string): string => {
	// Role name specifed in CDK's API stack
	// https://code.amazon.com/packages/AWSHammerstoneUICDK/blobs/8f330b0573d078f559e3ff816bb45c9bc8ee803c/--/lib/apiAuthStack.ts#L63
	return `arn:${partition}:iam::${account}:role/HammerstoneAPITempCreds`;
};
